

import { Ref, computed, ref } from 'vue';
import { Suggestion as SuggestionDTO } from '@/interfaces/suggestion.interface';
import globals from '@/utils/global';
import PathUtils from '@/utils/PathUtils';
import { ISuggestionMatchResult } from '@/interfaces/suggestion-match-result.interface'
import HandleSuggestionKeyboardEvent from '../../../utils/HandleSuggestionKeyboardEvent';
import store from '@/store';
import { KeyboardKeys } from '@/enums/keyboard-keys.enum';
import Tracking from '@/infrastructure/tracking/Tracking';
import { EventAggregationService } from '@/utils/EventAggregationService';
import { InlineSearchSuggestionClickEvent } from '@studyportals/inlinesearch-interfaces';

export default class Suggestion {
	constructor(public suggestion: Ref<SuggestionDTO>, public index: Ref<number>) {}
	suggestionRef = ref<HTMLElement | null>(null);
	keyboardEvent = computed(() => store.getters.getUserKeyboardEvent());

	onKeyDownChange = (event: KeyboardEvent | null): void => {
		if(this.index.value !== 0) return;

		const key = event?.key as KeyboardKeys;
		if (key !== KeyboardKeys.ARROW_DOWN) return;
		event?.preventDefault();
		this.suggestionRef?.value?.setAttribute("tabindex", "0");
		this.suggestionRef?.value?.focus();
	}

	onKeyDown(event: KeyboardEvent): void {
		HandleSuggestionKeyboardEvent.handle(event, this.suggestionRef.value);
	}

	public async onSuggestionClick(event: Event): Promise<void> {
		const target = event.currentTarget as HTMLElement;
		const text = target.textContent || "";

		Tracking.suggestionClick(
			this.index.value,
			text,
			store.getters.getText(),
		);

		EventAggregationService.instance?.publishTo(
			InlineSearchSuggestionClickEvent.EventType,
			new InlineSearchSuggestionClickEvent(text)
		);
		await store.actions.updateProgrammesFromSuggestions(this.setRawText(text));

	}
	private setRawText = (text: string): string => {
		return text.replace(/and/g, " & ").replace(/\s\s+/g, " ");
	}

	private normalizeText = (text: string): string => {
		// Replace "&" with ' and ' and collapse multiple spaces into one
		return text.replace(/&/g, " and ").replace(/\s\s+/g, " ");
	}

	isSearchPage = computed((): boolean => PathUtils.isInSearchPage());
	isUniversitySearch = computed((): boolean => PathUtils.IsUniversitySearch());
	isScholarshipSearch = computed((): boolean => PathUtils.isScholarshipSearch());

	private processMatches = (
		originalText: string,
		matchResults: string[]
	): ISuggestionMatchResult[] => {
		const results: ISuggestionMatchResult[] = [];
		const originalTextLower = originalText.trim().toLowerCase();
		for (const match of matchResults) {
			const matchLower = match.trim().toLowerCase();
			if (!match) continue;

			let className = "SuggestedText";

			if (matchLower.includes(originalTextLower)) {
				className = "Original";
			}

			results.push({ class: className, text: match });
		}

		return results;
	};

	completeSuggestionMatch = computed(() => {
		const { originalText, fullText } = this.suggestion.value;
		const textWithoutHtmlTags = fullText.replace(globals.removeHtmlTagsRegex,"");
		const sanitizedFullText = this.normalizeText(textWithoutHtmlTags);
		const matchesResults = sanitizedFullText.split(globals.suggestionsMatchRegexBuilder(originalText));
		return this.processMatches(originalText, matchesResults);
	});

	portalPathFragment = computed((): string => PathUtils.portalPathFragment());
}